import {
  GroupSecurity,
  isPeriodComplete,
  type Period,
} from '@quromedical/fhir-common';
import type { UpdateChargeItemDefinition } from '@quromedical/models';
import {
  ChargeItemDefinitionCard,
  type ChargeItemDefinition as FormData,
  type InitialChargeItemDefinition,
  mapFormDataToChargeItemRequest,
} from 'core/claim';
import { Section, Skeleton, SkeletonProvider } from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { useRevalidation } from 'hooks/useRevalidation';
import { useUserSession } from 'hooks/useUserSession';
import {
  administratorCodeFetcher,
  organizationSchemeCodeFetcher,
} from 'integration';
import { ChargeItemApi } from 'integration/aggregate';
import type { ChargeItemStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import useSWR from 'swr';

const api = new ChargeItemApi();

const getEndDateOfPeriod = (period: Period | undefined): string | undefined => {
  return period && isPeriodComplete(period) ? period.end : undefined;
};

type RouteParams = {
  id: string;
};

export const ChargeItemScreen: React.FC<
  ChargeItemStackScreenProps<'View'>
> = () => {
  const id = useLocalSearchParams<RouteParams>().id;

  const session = useUserSession();
  const canEdit = session.hasAny([GroupSecurity.ChargeItemDefinitionUpdate]);

  const fetcher = useCallback(() => api.getChargeItem(id), [id]);
  const swrKey = `trpc/medical-aid/charge-item/${id}`;

  const { data, isValidating, error } = useSWR(swrKey, fetcher);

  const revalidate = useRevalidation('trpc/medical-aid/charge-item');

  const handleSubmit = useCallback(
    async (data: FormData) => {
      const requestData = mapFormDataToChargeItemRequest(data);

      const updateRequest: UpdateChargeItemDefinition = {
        ...requestData,
        id,
        status: 'active',
      };

      await api.updateChargeItem(updateRequest);
      await revalidate();
    },
    [id, revalidate],
  );

  if (error) {
    return <NotFoundScreen />;
  }

  const loading = isValidating || !data;

  const completePeriodEnd = getEndDateOfPeriod(data?.effectivePeriod);

  const initial: InitialChargeItemDefinition | undefined = data
    ? {
        title: data.title,
        description: data.description,
        chargeLines: data.chargeLines.map((line) => {
          const identifier = {
            code:
              line.applicability?.schemeCode ||
              line.applicability?.administratorCode,
            display: line.applicability?.description,
          };

          return {
            amount: line.amount?.value,
            amountFactor: line.amount?.factor,
            currency: line.amount?.currency,
            chargeCode: line.code,
            type: line.applicability?.schemeCode ? 'scheme' : 'administrator',
            identifierCode: identifier.code,
            identifierDisplay: identifier.display,
          };
        }),
        effectivePeriodStart: data.effectivePeriod?.start,
        effectivePeriodEnd: completePeriodEnd,
      }
    : undefined;

  return (
    <ScrollView>
      <SkeletonProvider loading={loading}>
        <Section unsetZIndex>
          <Skeleton>
            <ChargeItemDefinitionCard
              administratorFetcher={administratorCodeFetcher}
              canEdit={canEdit}
              schemeFetcher={organizationSchemeCodeFetcher}
              initial={initial}
              onSubmit={handleSubmit}
            />
          </Skeleton>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
